/**
 * @module localStorage
 * @description Implements the local-first AI adapter used for demos,
 * prototyping, and Storybook scenarios. The adapter stores chat history in
 * browser `localStorage` and returns deterministic canned responses.
 */

import {
  getBrowserLocalStorage,
  hasBrowserLocalStorage,
  isBrowserStorageQuotaError,
} from "../browser-storage";
import { AiError } from "../errors";
import { warnRuntimeAdapterMock } from "../runtime-warning";
import type { AiOptions, ChatMessage, ChatResponse } from "../types";
import type { AiAdapter } from "./interface";

const HISTORY_KEY = "geenius_ai_history";
const DEFAULT_RATE_LIMIT_MESSAGE =
  "Local AI mock rate limit exceeded for this test scenario.";

/**
 * Context passed to local AI mock response resolvers.
 */
export interface LocalStorageAiResponseContext {
  /** Messages supplied to the current chat or stream request. */
  messages: readonly ChatMessage[];
  /** Inference options supplied by the caller, when present. */
  options?: AiOptions;
  /** One-based successful request count for this adapter instance. */
  requestCount: number;
}

/**
 * Controls deterministic rate-limit simulation for the local AI mock.
 */
export interface LocalStorageAiRateLimitOptions {
  /** Number of successful AI operations allowed before RATE_LIMIT is thrown. */
  afterRequests?: number;
  /** Error message used when the configured limit is reached. */
  message?: string;
}

/**
 * Options for configuring the localStorage-backed AI mock.
 */
export interface LocalStorageAiAdapterOptions {
  /** Deterministic response pool used when no custom resolver is supplied. */
  responses?: readonly string[];
  /** Optional resolver for per-request mock content. */
  resolveResponse?: (context: LocalStorageAiResponseContext) => string;
  /** Enables deterministic RATE_LIMIT failures for tests and demos. */
  rateLimit?: boolean | LocalStorageAiRateLimitOptions;
}

interface StoredHistoryEntry {
  timestamp: string;
  messages: ChatMessage[];
  response: string;
}

interface HistoryStore {
  read(): StoredHistoryEntry[];
  write(history: StoredHistoryEntry[]): void;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

const CANNED_RESPONSES = [
  "That's an interesting question! In Pronto, AI responses are simulated. Upgrade to Lancio or Studio for a real AI provider.",
  "I'm a mock AI assistant. I can demonstrate the chat interface, but real responses require a production AI provider in Lancio or Studio.",
  "Great question! This is a localStorage-based AI mock. The real AI adapter connects to OpenAI/Anthropic APIs.",
  "I appreciate your input! This demo shows how the AI interface works. Upgrade for actual AI capabilities.",
  "Interesting! While I can't provide real AI analysis in Pronto, this shows the adapter pattern in action.",
];

function isChatMessage(value: unknown): value is ChatMessage {
  if (!isRecord(value)) {
    return false;
  }

  return (
    typeof value.role === "string" &&
    ["system", "user", "assistant"].includes(value.role) &&
    typeof value.content === "string"
  );
}

function validateChatMessages(messages: unknown): ChatMessage[] {
  if (!Array.isArray(messages) || messages.length === 0) {
    throw new AiError("AI messages must be a non-empty array", "INVALID_INPUT");
  }
  if (!messages.every(isChatMessage)) {
    throw new AiError(
      "AI messages must include valid role and content fields",
      "INVALID_INPUT",
    );
  }
  return messages.map((message) => ({
    role: message.role,
    content: message.content,
  }));
}

function validateEmbeddingInput(text: unknown): string[] {
  const inputs = Array.isArray(text) ? text : [text];
  if (
    inputs.length === 0 ||
    !inputs.every((item) => typeof item === "string" && item.length > 0)
  ) {
    throw new AiError(
      "AI embedding input must be a non-empty string or non-empty string array",
      "INVALID_INPUT",
    );
  }
  return inputs;
}

function isStoredHistoryEntry(value: unknown): value is StoredHistoryEntry {
  if (!isRecord(value)) {
    return false;
  }

  return (
    typeof value.timestamp === "string" &&
    Array.isArray(value.messages) &&
    value.messages.length > 0 &&
    value.messages.every(isChatMessage) &&
    typeof value.response === "string"
  );
}

function readBrowserHistory(): StoredHistoryEntry[] {
  const storage = getBrowserLocalStorage();
  const parsed: unknown = JSON.parse(storage.getItem(HISTORY_KEY) || "[]");
  if (!Array.isArray(parsed) || !parsed.every(isStoredHistoryEntry)) {
    throw new AiError(
      "Invalid AI history payload in localStorage",
      "SERVICE_ERROR",
    );
  }
  return parsed;
}

function createMemoryHistoryStore(): HistoryStore {
  let history: StoredHistoryEntry[] = [];

  return {
    read() {
      return structuredClone(history);
    },
    write(nextHistory) {
      history = structuredClone(nextHistory);
    },
  };
}

function createBrowserHistoryStore(): HistoryStore {
  return {
    read: readBrowserHistory,
    write(history) {
      const storage = getBrowserLocalStorage();
      storage.setItem(HISTORY_KEY, JSON.stringify(history));
    },
  };
}

function createHistoryStore(): HistoryStore {
  return hasBrowserLocalStorage()
    ? createBrowserHistoryStore()
    : createMemoryHistoryStore();
}

function getResponsePool(
  options: LocalStorageAiAdapterOptions,
): readonly string[] {
  return options.responses && options.responses.length > 0
    ? options.responses
    : CANNED_RESPONSES;
}

function getCannedResponse(
  messages: ChatMessage[],
  options: LocalStorageAiAdapterOptions,
  aiOptions: AiOptions | undefined,
  requestCount: number,
): string {
  const resolved = options.resolveResponse?.({
    messages,
    options: aiOptions,
    requestCount,
  });
  if (typeof resolved === "string") {
    return resolved;
  }

  const responses = getResponsePool(options);
  const lastMsg = messages[messages.length - 1]?.content || "";
  // Deterministic but varied: hash the message to pick a response
  let hash = 0;
  for (let i = 0; i < lastMsg.length; i++) {
    hash = (hash << 5) - hash + lastMsg.charCodeAt(i);
    hash |= 0;
  }
  return (
    responses[Math.abs(hash) % responses.length] ??
    responses[0] ??
    CANNED_RESPONSES[0] ??
    ""
  );
}

function getRateLimitThreshold(
  options: LocalStorageAiAdapterOptions,
): number | null {
  if (options.rateLimit === undefined || options.rateLimit === false) {
    return null;
  }
  if (options.rateLimit === true) {
    return 0;
  }

  const { afterRequests = 0 } = options.rateLimit;
  return Math.max(0, afterRequests);
}

function assertWithinRateLimit(
  options: LocalStorageAiAdapterOptions,
  requestCount: number,
): void {
  const threshold = getRateLimitThreshold(options);
  if (threshold === null || requestCount < threshold) {
    return;
  }

  const message =
    options.rateLimit && typeof options.rateLimit === "object"
      ? options.rateLimit.message
      : undefined;
  throw new AiError(message ?? DEFAULT_RATE_LIMIT_MESSAGE, "RATE_LIMIT");
}

function saveToHistory(
  store: HistoryStore,
  messages: ChatMessage[],
  response: string,
): void {
  try {
    const validatedMessages = validateChatMessages(messages);
    const history = store.read();
    history.push({
      timestamp: new Date().toISOString(),
      messages: validatedMessages,
      response,
    });
    // Keep last 50 conversations
    if (history.length > 50) history.splice(0, history.length - 50);
    store.write(history);
  } catch (error) {
    if (error instanceof AiError) {
      throw error;
    }
    if (isBrowserStorageQuotaError(error)) {
      throw new AiError("localStorage quota exceeded", "SERVICE_ERROR", error);
    }
    if (error instanceof SyntaxError) {
      throw new AiError(
        "Failed to parse AI history from localStorage",
        "SERVICE_ERROR",
        error,
      );
    }
    throw new AiError(
      "Failed to write AI history to localStorage",
      "SERVICE_ERROR",
      error,
    );
  }
}

/**
 * Creates the mock localStorage-backed AI adapter used in demos and Storybook.
 *
 * @param options - Optional deterministic response and rate-limit simulation controls.
 * @returns An AI adapter implementation that emits deterministic canned responses and stores history locally.
 * Falls back to isolated in-memory history when browser storage is unavailable
 * during SSR or edge rendering.
 *
 * @throws {AiError} When browser storage quota exhaustion prevents history persistence.
 * @throws {AiError} With `RATE_LIMIT` when configured rate-limit simulation is reached.
 */
export function createLocalStorageAiAdapter(
  options: LocalStorageAiAdapterOptions = {},
): AiAdapter {
  if (
    typeof process !== "undefined" &&
    process.env?.NODE_ENV === "production"
  ) {
    warnRuntimeAdapterMock(
      "[@geenius/adapters] localStorage AI adapter is a MOCK for prototyping only. " +
        "Do NOT use in production. Use a real AI provider from @geenius/ai instead.",
    );
  }

  const historyStore = createHistoryStore();
  let requestCount = 0;

  function startRequest(): number {
    assertWithinRateLimit(options, requestCount);
    requestCount += 1;
    return requestCount;
  }

  return {
    async chat(
      messages: ChatMessage[],
      aiOptions?: AiOptions,
    ): Promise<ChatResponse> {
      const validatedMessages = validateChatMessages(messages);
      const currentRequest = startRequest();
      const content = getCannedResponse(
        validatedMessages,
        options,
        aiOptions,
        currentRequest,
      );
      saveToHistory(historyStore, validatedMessages, content);
      return {
        content,
        finishReason: "stop",
        usage: { promptTokens: 10, completionTokens: 20, totalTokens: 30 },
      };
    },

    async complete(prompt: string, options?: AiOptions): Promise<string> {
      const response = await this.chat(
        [{ role: "user", content: prompt }],
        options,
      );
      return response.content;
    },

    async embed(text: string | string[]): Promise<number[][]> {
      const inputs = validateEmbeddingInput(text);
      startRequest();
      return inputs.map((t) => {
        const vec: number[] = [];
        for (let i = 0; i < 8; i++) {
          let h = i * 31;
          for (let j = 0; j < t.length; j++) {
            h = (h << 5) - h + t.charCodeAt(j);
            h |= 0;
          }
          vec.push(Math.sin(h) * 0.5 + 0.5);
        }
        return vec;
      });
    },

    async stream(
      messages: ChatMessage[],
      aiOptions?: AiOptions,
    ): Promise<AsyncIterable<string>> {
      const validatedMessages = validateChatMessages(messages);
      const currentRequest = startRequest();
      async function* generateStream(): AsyncIterable<string> {
        const content = getCannedResponse(
          validatedMessages,
          options,
          aiOptions,
          currentRequest,
        );
        saveToHistory(historyStore, validatedMessages, content);
        const words = content.split(" ");
        for (const word of words) {
          yield `${word} `;
        }
      }

      return generateStream();
    },
  };
}
